import { Box } from "@mui/material";
import { colors } from "@procore/core-react";
import { useEffect, useRef } from "react";

import { useExplorerGlobals } from "contexts/explorerGlobals";

/** Distance from the pointer to the prompt, so the pill never sits under the cursor itself. */
const CURSOR_GAP = { x: 16, y: -6 };

const pillSx = {
    position: "absolute",
    borderRadius: "999px",
    padding: "8px 12px",
    background: colors.blue50,
    color: colors.white,
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "16px",
    letterSpacing: "0.25px",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    zIndex: 1052,
} as const;

/** Centred under whatever sits at the top of the screen - the touch placement, where there is no cursor to follow. */
export function AnchoredPickPrompt({ text, top }: { text: string; top: string }) {
    return <Box sx={{ ...pillSx, top, left: "50%", translate: "-50% 0" }}>{text}</Box>;
}

/**
 * Trails the pointer across the 3D canvas. Positioned by writing to the element directly rather
 * than through state, so following the mouse does not re-render on every move.
 */
export function CursorPickPrompt({ text }: { text: string }) {
    const {
        state: { canvas },
    } = useExplorerGlobals();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = ref.current;
        if (!canvas || !element) {
            return;
        }

        // Stay hidden until the pointer is actually over the canvas, otherwise the prompt would sit
        // parked in a corner from the moment the tool is picked.
        const hide = () => {
            element.style.visibility = "hidden";
        };
        hide();

        const move = (e: PointerEvent) => {
            // Coordinates are relative to whatever the pill is positioned against, which is not
            // necessarily the canvas.
            const origin = (element.offsetParent as HTMLElement | null)?.getBoundingClientRect();
            const x = e.clientX - (origin?.left ?? 0);
            const y = e.clientY - (origin?.top ?? 0);

            // Flip to the other side of the cursor rather than run off the edge of the window.
            const flipX = e.clientX + CURSOR_GAP.x + element.offsetWidth > window.innerWidth;
            const flipY = e.clientY + CURSOR_GAP.y + element.offsetHeight > window.innerHeight;

            element.style.left = `${flipX ? x - CURSOR_GAP.x - element.offsetWidth : x + CURSOR_GAP.x}px`;
            element.style.top = `${flipY ? y - CURSOR_GAP.y - element.offsetHeight : y + CURSOR_GAP.y}px`;
            element.style.visibility = "visible";
        };

        canvas.addEventListener("pointermove", move);
        canvas.addEventListener("pointerleave", hide);

        return () => {
            canvas.removeEventListener("pointermove", move);
            canvas.removeEventListener("pointerleave", hide);
        };
    }, [canvas]);

    return (
        <Box ref={ref} sx={pillSx}>
            {text}
        </Box>
    );
}
